import { Calendar, SkHit, Unit } from 'sr-types/lib/search/v1/graphql';
import { AutocompleteOption } from '../../../form/widgets/OptionRendererProps';
import { SUPPORTED_SCHEDULER_DURATION_UNITS } from '../constants';
import { SupportedSchedulerDurationUnit } from '../types';

type SkHitWithOptionalLabel = SkHit & { label?: string };

/**
 * Maps Duration Unit search result hit items to options for an Autocomplete form widget.
 * @param durationUnits Search/Result Query hit items
 * @returns Options list for Autocomplete form widget
 */
export const mapDurationUnitsDataToOptions = (
    durationUnits: Unit[] = [],
    allowedDurationUnits: SupportedSchedulerDurationUnit[] = SUPPORTED_SCHEDULER_DURATION_UNITS
): AutocompleteOption[] => {
    const resolvedDurationUnits = durationUnits.filter((unit) => {
        return allowedDurationUnits.includes(unit.id as SupportedSchedulerDurationUnit);
    });

    const durationUnitOptions = mapSearchHitItemToAutocompleteOptions<Unit>(resolvedDurationUnits);

    return durationUnitOptions;
};

/**
 * Maps Calendar search result hit items to options for an Autocomplete form widget.
 * @param calendars Calendars search query hits
 * @returns Options list for Autocomplete form widget
 */
export function mapCalendarDataToOptions(calendars: Calendar[] = []): AutocompleteOption[] {
    return mapSearchHitItemToAutocompleteOptions<Calendar>(calendars);
}

function mapSearchHitItemToAutocompleteOptions<I extends SkHitWithOptionalLabel = SkHitWithOptionalLabel>(
    items: I[]
): AutocompleteOption[] {
    return items
        .map((item) => {
            const label = extractSearchHitItemLabel(item);
            const id = item.id;

            if (!label || !id) {
                return null;
            }

            return {
                id,
                label
            };
        })
        .filter((option): option is AutocompleteOption => Boolean(option));
}

function extractSearchHitItemLabel<I extends SkHitWithOptionalLabel = SkHitWithOptionalLabel>(item: I): string {
    const unitLabel = item.label ?? item.reference?.label ?? item.name;

    if (!unitLabel) {
        console.warn(`Search hit item with id ${item.id} is missing a label or name.`, item);
        return 'Unknown';
    }

    return unitLabel;
}
